import { DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { GeneralService } from 'src/app/services/general.service';
import { OperationRentalService } from 'src/app/services/Rentals.service';
import { ReportsService } from 'src/app/services/report.service';
import { EActionTypes, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { IReportResponse } from 'src/app/Shared/model/ReportsModel';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES } from 'src/app/Shared/shared-imports';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { WordReplacePipe } from 'src/app/Shared/word-replace.pipe';
import { TopBarComponent } from 'src/app/top-bar/top-bar.component';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-ops-deposit-slip',
  standalone: true,
  imports: [TopBarComponent, SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, WordReplacePipe, UserPermissionHandlerDirective],
  templateUrl: './ops-deposit-slip.html',
  styleUrl: './ops-deposit-slip.scss'
})

export class OpsDepositSlip implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  isCollapsed: boolean = true;
  depositSlipForm!: FormGroup;
  filteredData: any[] = [];
  depositSlipData: any[] = [];
  currentFetchedData: any[] = [];
  today: Date = new Date();
  totals = {
    cash: 0,
    ccDc: 0,
    checks: 0,
    debit: 0,
    grand: 0
  };
  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.DepositSlip, Action: EActionTypes.VIEW };
  reportUrl;
  marketId: any;
  public loaderIcon: Promise<boolean>;
  public datePipe = new DatePipe('en-US');

  constructor(
    private rentalDepositSlipService: OperationRentalService,
    private service: ReportsService,
    private generalService: GeneralService,
    private fb: FormBuilder,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.getReportUrl();
    this.rentalDepositSlipService.getDefaultMarketId().subscribe((res: any) => {
      if (res) {
        this.marketId = res;
      }
    });
    this.setupOptionTypeToggle();
    this.setupReactiveFetch();
    this.fetchByDate(this.today);
  }

  initForm(): void {
    this.depositSlipForm = this.fb.group({
      optionType: ['date', Validators.required],
      date: [new Date()],
      fromReceipt: [null],
      toReceipt: [null],
      filters: this.fb.group({
        cash: [true],
        checks: [true],
        card: [true],
        debit: [true]
      })
    });
  }

  getReportUrl() {
    this.generalService.getReportUrl().subscribe((response: any) => {
      this.reportUrl = response;
    });
  }

  invokeReport(obj) {
    var openInNewTab = '_blank';
    this.loaderIcon = Promise.resolve(true);
    this.service.generateReport(obj).subscribe((response: IReportResponse) => {
      if (response.Success) {
        window.open(this.reportUrl + `report?id=` + response.ReportRequestId,
          openInNewTab, 'width=' + (screen.availWidth - 5) + ',height=' + (screen.availHeight - 5));
        this.loaderIcon = Promise.resolve(false);
      } else {
        console.log('Error on generating Operational Report');
        this.loaderIcon = Promise.resolve(false);
      }
    });
  }

  onReportView() {
    const obj: any = {
      ReportName: "DepositSlipReport",
      ReportParameters: {},
      MarketId: this.marketId
    };

    obj.ReportParameters["MarketId"] = this.marketId;
    obj.ReportParameters["SystemDate"] = new Date().toISOString();
    obj.ReportParameters["StartReceiptId"] = "";
    obj.ReportParameters["EndReceiptId"] = "";
    obj.ReportParameters["ReportType"] = this.depositSlipForm.controls['optionType'].value;
    const optionType = this.depositSlipForm.controls['optionType'].value;

    if (optionType === 'date') {
      const systemDate = this.depositSlipForm.controls['date'].value;
      obj.ReportParameters["SystemDate"] = this.datePipe.transform(systemDate, 'MM/dd/yyyy');
    } else if (optionType === 'range') {
      const startReceipt = this.depositSlipForm.controls['fromReceipt'].value;
      const endReceipt = this.depositSlipForm.controls['toReceipt'].value;
      obj.ReportParameters["StartReceiptId"] = startReceipt;
      obj.ReportParameters["EndReceiptId"] = endReceipt;
    }

    const filters = this.depositSlipForm.get('filters').value;

    // Convert true/false to 1/0
    obj.ReportParameters["IncludeCash"] = filters.cash ? 1 : 0;
    obj.ReportParameters["IncludeCheck"] = filters.checks ? 1 : 0;
    obj.ReportParameters["IncludeCC"] = filters.card ? 1 : 0;
    obj.ReportParameters["IncludeDebit"] = filters.debit ? 1 : 0;

    //console.log("Report Parameters are:", obj.ReportParameters);
    //console.log("MarketId:", obj.MarketId);
    this.loaderIcon = Promise.resolve(false);
    this.invokeReport(obj);
  }

  setupOptionTypeToggle(): void {
    const optionTypeControl = this.depositSlipForm.get('optionType')!;
    this.toggleFetchControls(optionTypeControl.value);
    optionTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: string) => {
        this.toggleFetchControls(value);
      });
  }

  toggleFetchControls(optionType: string): void {
    if (optionType === 'date') {
      this.depositSlipForm.get('date')!.enable();
      this.depositSlipForm.get('date')!.setValue(new Date());
      this.depositSlipForm.get('fromReceipt')!.disable();
      this.depositSlipForm.get('toReceipt')!.disable();
      this.depositSlipForm.get('fromReceipt')!.reset();
      this.depositSlipForm.get('toReceipt')!.reset();
    } else if (optionType === 'range') {
      this.depositSlipForm.get('date')!.disable();
      this.depositSlipForm.get('fromReceipt')!.enable();
      this.depositSlipForm.get('toReceipt')!.enable();
      // this.depositSlipForm.get('date')!.reset();
      this.depositSlipForm.get('date')!.setValue(new Date());
      this.depositSlipData = [];
    }
  }

  preprocessData(data: any[]): any[] {
    return data.map(row => {
      const cash = Number(row.Cash ?? 0);
      const check = Number(row.Check ?? 0);
      const card = Number(row.CreditCard ?? 0);
      const debit = Number(row.Debit ?? 0);
      const grand = Number(row.GrandTotal ?? 0);

      return {
        ...row,
        Cash: cash.toFixed(2),
        Check: check.toFixed(2),
        CreditCard: card.toFixed(2),
        Debit: debit.toFixed(2),
        GrandTotal: grand.toFixed(2),
        CashRaw: cash,
        CheckRaw: check,
        CreditCardRaw: card,
        DebitRaw: debit,
        // GrandTotalRaw: grand,
        CheckNumber: row.CheckNumber ?? '-------'
      };
    });
  }

  setupReactiveFetch(): void {
    this.depositSlipForm.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroy$))
      .subscribe(values => {
        const { optionType, date, fromReceipt, toReceipt } = values;
        if (optionType === 'date') {
          if (date) {
            this.fetchByDate(date);
          }
        }
        else if (optionType === 'range') {
          if (!fromReceipt || !toReceipt) {
            this.depositSlipData = [];
            this.totals = {
              cash: 0,
              ccDc: 0,
              checks: 0,
              debit: 0,
              grand: 0
            };
            return;
          }
          if (fromReceipt > toReceipt) {
            Swal.fire({
              icon: 'warning',
              title: 'Invalid Range',
              text: 'From Receipt ID cannot be greater than To Receipt ID!'
            });

            this.depositSlipForm.patchValue({
              fromReceipt: null,
              toReceipt: null,
            });
            this.totals = {
              cash: 0,
              ccDc: 0,
              checks: 0,
              debit: 0,
              grand: 0
            };

            return;
          }
          if (fromReceipt <= toReceipt) {
            this.fetchByRange(fromReceipt, toReceipt);
          }
          else {
            this.depositSlipData = [];
          }

        }
      });
  }

  fetchByDate(date: Date): void {
    this.rentalDepositSlipService
      .getRentalDepositSlipByDate(this.formatDate(date))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.currentFetchedData = this.preprocessData(res ?? []);
          this.applyFilters();
        }
      });
  }

  fetchByRange(from: number, to: number): void {
    this.rentalDepositSlipService
      .getRentalDepositSlipByRange(from, to)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.currentFetchedData = this.preprocessData(res ?? []);
          this.applyFilters();
        },
        error: err => {
          console.error('Error fetching deposit slips by range', err);
          this.currentFetchedData = [];
        }
      });
  }

  applyFilters(): void {
    const filters = this.depositSlipForm.get('filters')!.value;
    const activeKeys = Object.keys(filters).filter(key => filters[key]);

    this.depositSlipData = this.currentFetchedData
      .map(row => {
        let rowTotal = 0;
        if (filters.cash) rowTotal += Number(row.CashRaw) || 0;
        if (filters.checks) rowTotal += Number(row.CheckRaw) || 0;
        if (filters.card) rowTotal += Number(row.CreditCardRaw) || 0;
        if (filters.debit) rowTotal += Number(row.DebitRaw) || 0;

        return {
          ...row,
          GrandTotal: rowTotal.toFixed(2)
        };
      })
      .filter(row =>
        activeKeys.some(key => {
          if (key === 'cash') return row.CashRaw !== 0;
          if (key === 'checks') return row.CheckRaw !== 0;
          if (key === 'card') return row.CreditCardRaw !== 0;
          if (key === 'debit') return row.DebitRaw !== 0;
          return false;
        })
      );

    this.totals = {
      cash: filters.cash ? this.depositSlipData.reduce((sum, x) => sum + (x.CashRaw || 0), 0) : 0,
      checks: filters.checks ? this.depositSlipData.reduce((sum, x) => sum + (x.CheckRaw || 0), 0) : 0,
      ccDc: filters.card ? this.depositSlipData.reduce((sum, x) => sum + (x.CreditCardRaw || 0), 0) : 0,
      debit: filters.debit ? this.depositSlipData.reduce((sum, x) => sum + (x.DebitRaw || 0), 0) : 0,
      grand: this.depositSlipData.reduce((sum, x) => {
        let subtotal = 0;
        if (filters.cash) subtotal += x.CashRaw || 0;
        if (filters.checks) subtotal += x.CheckRaw || 0;
        if (filters.card) subtotal += x.CreditCardRaw || 0;
        if (filters.debit) subtotal += x.DebitRaw || 0;
        return sum + subtotal;
      }, 0)
    };
  }

  formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`; // e.g. "2025-08-29"
  }

  formatAccountingCurrency(value: number | null): string {
    const num = value ?? 0;
    if (num === 0) return `$0.00`;
    if (num < 0) {
      return `($${Math.abs(num).toFixed(2)})`;
    }
    return `$${num.toFixed(2)}`;
  }

  closeDepositSlipPage(): void {
    this.isCollapsed = true;
    this.router.navigate(['/dashboard']);
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
